import { inject, Injectable, Injector } from '@angular/core';

import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { combineLatest, map, of, Subject, tap } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import { SelectLocationsDialogComponent, SelectLocationsSelectMode } from '@core/locations-legacy';
import { JobsWrapperDataService } from '@core/offers-data';
import type { JobItem } from '@core/offers-types';
import { TalentPoolsDataService } from '@core/talent-pools-data';
import type { TalentPoolBrief } from '@core/talent-pools-types';
import { DialogService, DialogWidth } from '@core/user-interface-legacy';

import type { OfferSelectDialogItem } from '../offer-select/offer-select.types';
import { OffersSelectDialogFormComponent } from '../offer-select/offers-select-dialog-form/offers-select-dialog-form.component';

type Data<T> = {
  jobAssignments: Assignment[];
  talentPoolAssignments: Assignment[];
  options: T;
  heading?: string;
  subheading?: string;
  skipOfferSelection?: boolean;
  isSelectionAlwaysSaveable?: boolean;
  skipLocationSelectionForUpdated?: boolean;
};

export type Assignment = {
  id: number;
  locationIds?: number[] | null;
  placementId?: number;
};

type AssignmentChanges = {
  updated: Assignment[];
  created: Assignment[];
  deleted: Assignment[];
};

@Injectable()
export abstract class AssignOffersFacade<T extends {}> {
  private readonly talentPoolsData = inject(TalentPoolsDataService);
  private readonly injector = inject(Injector);

  protected readonly jobsWrapperDataService = inject(JobsWrapperDataService);
  protected readonly actions$ = inject(Actions);
  protected readonly store = inject(Store);
  protected readonly i18n = inject(I18nService);
  protected readonly dialogService = inject(DialogService);

  public hasOffersLoaded$ = combineLatest([
    this.jobsWrapperDataService.getActiveJobItemsIsLoaded$(),
    this.talentPoolsData.meta$(),
  ]).pipe(map(([jobsLoaded, talentPoolsMeta]) => jobsLoaded && talentPoolsMeta.isLoaded));
  protected offers: OfferSelectDialogItem[];
  protected jobAssignments: Assignment[];
  protected talentPoolAssignments: Assignment[];
  protected jobInitialAssignments: Assignment[] = [];
  protected talentPoolInitialAssignments: Assignment[] = [];
  protected options: T;

  protected selectedLocationsByOffer = new Map<number, number[]>();
  protected jobAssignmentsChanges: AssignmentChanges = { updated: [], created: [], deleted: [] };
  protected talentPoolAssignmentsChanges: AssignmentChanges = {
    updated: [],
    created: [],
    deleted: [],
  };

  protected ngOnDestroy$ = new Subject<void>();

  private heading: string = this.i18n.translate('app.assignments.editor.title');
  private subheading: string = this.i18n.translate('app.assignments.editor.description');

  private skipOfferSelection: boolean = false;
  private offersToAssignLocation: JobItem[] = [];
  private currentStepIndex: number = 0;
  private skipLocationSelectionForUpdated: boolean = false;
  private isSelectionAlwaysSaveable: boolean = false;

  public abstract dispatch(): Observable<boolean>;

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public execute(data: Data<T>): void {
    this.reset();
    combineLatest([
      this.jobsWrapperDataService.getActiveJobItemsCanAssignTo$(),
      this.talentPoolsData.collection$().pipe(filterBoolean),
    ])
      .pipe(take(1))
      .subscribe(([jobs, talentPools]) => {
        this.jobAssignments = data.jobAssignments;
        this.talentPoolAssignments = data.talentPoolAssignments;
        if (data.heading) this.heading = data.heading;
        this.subheading =
          data.subheading ?? this.i18n.translate('app.assignments.editor.description');
        this.offers = [...jobs, ...talentPools];
        this.options = data.options;
        this.skipOfferSelection = !!data?.skipOfferSelection;
        this.skipLocationSelectionForUpdated = !!data?.skipLocationSelectionForUpdated;
        this.isSelectionAlwaysSaveable = !!data?.isSelectionAlwaysSaveable;
        if (data?.skipOfferSelection)
          this.onOffersSelected(data.jobAssignments, data.talentPoolAssignments)
            .pipe(take(1))
            .subscribe();
        else {
          this.setInitialJobAssignments(data.jobAssignments);
          this.talentPoolInitialAssignments = [...data.talentPoolAssignments];
          this.selectOffers();
        }
      });
  }

  private reset() {
    this.jobAssignments = [];
    this.talentPoolAssignments = [];
    this.currentStepIndex = 0;
    this.selectedLocationsByOffer = new Map<number, number[]>();
    this.jobAssignmentsChanges = { updated: [], deleted: [], created: [] };
    this.talentPoolAssignmentsChanges = { updated: [], deleted: [], created: [] };
    this.offersToAssignLocation = [];
    this.skipOfferSelection = false;
  }

  private selectOffers(): void {
    this.dialogService.custom(
      OffersSelectDialogFormComponent,
      { width: DialogWidth.Small, injector: this.injector },
      {
        heading: this.heading,
        subheading: this.subheading,
        submitLabel: this.i18n.translate('app.common.continue'),
        jobAssignments: this.jobAssignments.map(assignment => assignment.id),
        talentPoolAssignments: this.talentPoolAssignments.map(assignment => assignment.id),
        offers: this.offers,
        isAlwaysSaveable: this.isSelectionAlwaysSaveable,
        hasInitialAssignments: false,
        saveHandler$: (offerIds: number[], talentPoolIds: number[]) => {
          const jobAssignments = offerIds.map(id => ({
            id,
            locationIds: this.selectedLocationsByOffer.get(id) || [],
          }));
          const talentPoolAssignments = talentPoolIds.map(id => ({ id }));
          return this.onOffersSelected(jobAssignments, talentPoolAssignments);
        },
      },
    );
  }

  private onOffersSelected(
    offerIds: Assignment[],
    talentPoolIds: Assignment[],
  ): Observable<boolean> {
    this.jobAssignments = offerIds;
    this.talentPoolAssignments = talentPoolIds;
    this.jobAssignmentsChanges = this.getChanges(offerIds, this.jobInitialAssignments);
    this.talentPoolAssignmentsChanges = this.getChanges(
      talentPoolIds,
      this.talentPoolInitialAssignments,
    );

    const addedJobs$ = this.getAddedJobs$();
    const addedTalentPools$ = this.talentPoolsData
      .briefEntitiesByIds$(
        this.talentPoolAssignmentsChanges.updated.map(assignment => assignment.id),
      )
      .pipe(map((talentPools: TalentPoolBrief[]) => talentPools.filter(v => !!v)));

    return combineLatest([addedJobs$, addedTalentPools$]).pipe(
      take(1),
      tap(() => (this.currentStepIndex = 0)),
      switchMap(() => {
        const hasAdditionalSteps = !!this.offersToAssignLocation.length;
        if (hasAdditionalSteps) {
          this.runStep();
          return of(true);
        } else {
          return this.dispatch();
        }
      }),
    );
  }

  private getChanges(
    newAssignments: Assignment[],
    currentAssignments: Assignment[],
  ): { updated: Assignment[]; deleted: Assignment[]; created: Assignment[] } {
    return {
      deleted: currentAssignments.filter(
        currentAssignment =>
          !newAssignments.find(assignment => assignment.id === currentAssignment.id),
      ),
      created: newAssignments.filter(
        newAssignment => !currentAssignments.find(assignment => assignment.id === newAssignment.id),
      ),
      updated: [...newAssignments],
    };
  }

  private runStep(): void {
    const offer = this.offersToAssignLocation[this.currentStepIndex];
    this.dialogService.openCustom(
      SelectLocationsDialogComponent,
      { width: DialogWidth.SmallX },
      {
        initialAssignedLocationIds: this.selectedLocationsByOffer.get(offer.id) || [],
        selectMode: SelectLocationsSelectMode.Multi,
        availableLocationIds: offer.locationIds,
        heading: offer.title,
        subheading: this.i18n.translate('app.assignments.editor.work_location_subheading'),
        submitLabel: this.i18n.translate('app.common.continue'),
        hasSkip: true,
        isAlwaysSaveable: true,
        saveHandler$: (locationIds: number[]) => {
          this.selectedLocationsByOffer.set(offer.id, locationIds);
          this.currentStepIndex++;
          if (this.currentStepIndex > this.offersToAssignLocation.length - 1) {
            return this.dispatch();
          } else {
            this.runStep();
            return of(true);
          }
        },
        goBackHandler:
          this.skipOfferSelection && this.currentStepIndex === 0
            ? undefined
            : () => {
                if (this.currentStepIndex > 0) {
                  this.currentStepIndex--;
                  this.runStep();
                } else {
                  this.selectOffers();
                }
              },
      },
    );
  }

  private getAddedJobs$(): Observable<JobItem[]> {
    const offerIds = (
      this.skipLocationSelectionForUpdated
        ? this.jobAssignmentsChanges.created
        : this.jobAssignmentsChanges.updated
    ).map(assigment => assigment.id);

    return this.jobsWrapperDataService.getJobItemsByIds$(offerIds).pipe(
      map((jobs: JobItem[]) => jobs.filter(job => !!job)),
      tap(jobs => {
        jobs.forEach(job => this.setAutomaticallySelectedLocationsByOffer(job));
        this.offersToAssignLocation = this.getOffersToAssignLocation(jobs);
      }),
    );
  }

  private setInitialJobAssignments(assignments: Assignment[]): void {
    this.jobInitialAssignments = assignments;
    assignments.forEach(assignment => {
      this.selectedLocationsByOffer.set(assignment.id, assignment.locationIds || []);
    });
  }

  private setAutomaticallySelectedLocationsByOffer(job: JobItem): void {
    const activeLocations = job.locations?.filter(location => !location.archivedAt) || [];
    if (activeLocations.length === 1)
      this.selectedLocationsByOffer.set(
        job.id,
        activeLocations.map(location => location.id),
      );
  }

  private getOffersToAssignLocation(jobs: JobItem[]): JobItem[] {
    return jobs.filter(
      job =>
        job.locations &&
        job.locations.filter(location => !location.archivedAt).length > 1 &&
        !this.selectedLocationsByOffer.get(job.id)?.length,
    );
  }
}
